import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule, DecimalPipe } from "@angular/common";
import { format, startOfYear, subDays } from "date-fns";
import { Chart, registerables } from "chart.js";
import { HeaderDialogComponent } from "../../../../shared/modules/dialog/header-dialog/header-dialog.component";
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { MultiCheckboxComponent } from "../../../../shared/modules/multi-checkbox/multi-checkbox.component";
import { ExcelExportButtonComponent } from "../../../../shared/components/excel-export-button/excel-export-button.component";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { firstValueFrom, forkJoin } from "rxjs";
import { exportEvolutionValeurExcel } from "./evolution-valeur-excel.utils";
import {
  ExcelExportConfirmDialogComponent,
  ExcelExportConfirmData,
  ExcelExportConfirmResult,
} from "../../../../shared/components/excel-export-confirm-dialog/excel-export-confirm-dialog.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import {
  EVOLUTION_VALEUR_DATA,
  EvolutionDimension,
  EvolutionGranularity,
  VALEUR_PAR_FAMILLE_DATA,
} from "../../models/data.model";
import { WidgetType } from "../../models/dashboard.model";
import {
  BackfillRange,
  WidgetDataApiService,
} from "../../services/widget-data-api.service";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { SnackService } from "../../../../shared/services/snack-service.service";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";

Chart.register(...registerables);

/**
 * Dialog "Voir tout" du widget EVOLUTION_VALEUR. Permet de :
 * - Choisir la **plage** (1m / 3m / 6m / exercice / 1a)
 * - Choisir la **granularité** temporelle (jour / semaine / mois /
 *   trimestre / année) — les options sont filtrées en fonction de la plage
 *   pour éviter les combinaisons illisibles (ex : year-by-year sur 1m)
 * - Choisir la **dimension** de découpage (total / famille / sous-famille /
 *   article / entrepôt / propriétaire)
 *
 * Affiche un line chart multi-séries avec les TOP N dimensions, un tableau
 * des valeurs courantes, et propose de relancer le calcul si l'historique
 * stocké est plus court que la plage demandée.
 *
 * # Données
 * Self-fetching via `WidgetDataApiService.loadEvolutionValeur()`. Le
 * backfill est délégué au composant parent (le bouton sync du widget
 * compact reste la source unique du recalcul) — depuis ce dialog, on
 * propose un CTA qui appelle `runBackfill()` puis refetch.
 */

interface DialogData {
  initialRange: BackfillRange;
}

const TOP_N = 7;
const PALETTE = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
  "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
];

@Component({
  selector: "vl-evolution-valeur-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    HeaderDialogComponent,
    ChipsToggleComponent,
    MultiCheckboxComponent,
    ExcelExportButtonComponent,
    MatIcon,
    MatTooltip,
    DecimalPipe,
  ],
  templateUrl: "./evolution-valeur-dialog.component.html",
  styleUrl: "./evolution-valeur-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvolutionValeurDialogComponent implements OnDestroy, AfterViewInit {
  data = inject<DialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<EvolutionValeurDialogComponent>);

  private api = inject(WidgetDataApiService);
  private snack = inject(SnackService);
  private dialogs = inject(DialogsService);

  /** Plage personnalisée — alternative à `range()`. Utilisée seulement à
   *  l'export. Quand non-null, on envoie `customStart`/`customEnd` à
   *  l'API pour le snapshot Excel (le widget compact reste sur la plage
   *  glissante par défaut). */
  exportCustomRange = signal<{ from: string; to: string } | null>(null);

  canvasRef = viewChild<ElementRef>("chart");

  range = signal<BackfillRange>(this.data.initialRange ?? "3m");
  granularity = signal<EvolutionGranularity>("day");
  dimension = signal<EvolutionDimension>("total");

  /** Filtre optionnel par dimension : liste des `dimensionKey` à conserver.
   *  Vide = aucun filtre (TOP N + "Autres" comme avant). */
  selectedKeys = signal<string[]>([]);

  /** Lignes "entrepôt" déployées pour montrer leurs sous-zones (PDC, zone
   *  d'expédition, zones NC). Le détail vient de `currentValeur` (état
   *  actuel) — il n'y a pas d'historique par sous-zone, le chart reste
   *  agrégé au niveau entrepôt parent. */
  expandedEntrepots = signal<Set<string>>(new Set());

  loading = signal<boolean>(true);
  refreshing = signal<boolean>(false);
  exporting = signal<boolean>(false);
  payload = signal<EVOLUTION_VALEUR_DATA | null>(null);
  /** Snapshot courant `VALEUR_PAR_FAMILLE` — utilisé pour le drill-down par
   *  sous-zone. Chargé seulement quand dimension = entrepot. */
  currentValeur = signal<VALEUR_PAR_FAMILLE_DATA | null>(null);
  chart: Chart | null = null;

  rangeElements: { name: string; value: BackfillRange }[] = [
    { name: "1M", value: "1m" },
    { name: "3M", value: "3m" },
    { name: "6M", value: "6m" },
    { name: "1A", value: "1y" },
    { name: "YTD", value: "ytd" },
    { name: "Exercice", value: "exercice" },
    { name: "Exercice passé", value: "exerciceN1" },
  ];

  /** Granularités proposées : on filtre celles qui n'ont pas de sens pour
   *  la plage. Ex : `year` n'a pas d'intérêt sur une plage < 2 ans, on
   *  l'exclut pour éviter un seul gros point. */
  granularityElements = computed(() => {
    const r = this.range();
    const all: { name: string; value: EvolutionGranularity }[] = [
      { name: "Jour", value: "day" },
      { name: "Semaine", value: "week" },
      { name: "Mois", value: "month" },
      { name: "Trimestre", value: "quarter" },
      { name: "Année", value: "year" },
    ];
    if (r === "1m") return all.filter((g) => ["day", "week"].includes(g.value));
    if (r === "3m") return all.filter((g) => ["day", "week", "month"].includes(g.value));
    if (r === "6m") return all.filter((g) => ["day", "week", "month"].includes(g.value));
    if (r === "ytd" || r === "exercice" || r === "exerciceN1")
      return all.filter((g) => ["day", "week", "month", "quarter"].includes(g.value));
    if (r === "1y")
      return all.filter((g) => ["week", "month", "quarter", "year"].includes(g.value));
    return all;
  });

  dimensionElements: { name: string; value: EvolutionDimension }[] = [
    { name: "Total", value: "total" },
    { name: "Famille", value: "famille" },
    { name: "Sous-famille", value: "sous_famille" },
    { name: "Article", value: "article" },
    { name: "Entrepôt", value: "entrepot" },
    { name: "Propriétaire", value: "owner" },
  ];

  /** Items proposés dans le multi-select de filtre, dérivés des séries
   *  effectivement chargées pour la dimension courante. Triés par valeur de
   *  fin décroissante pour mettre en avant les contributeurs majeurs. */
  filterElements = computed<{ name: string; value: string }[]>(() => {
    const d = this.payload();
    if (!d || this.dimension() === "total") return [];
    return [...d.series]
      .sort((a, b) => {
        const av = a.points[a.points.length - 1]?.total ?? 0;
        const bv = b.points[b.points.length - 1]?.total ?? 0;
        return bv - av;
      })
      .map((s) => ({ name: s.dimensionLabel, value: s.dimensionKey }));
  });

  /** Libellé du bouton multi-select : suit la dimension courante. */
  filterLabel = computed<string>(() => {
    const dim = this.dimension();
    return (
      this.dimensionElements.find((d) => d.value === dim)?.name ?? "Filtrer"
    );
  });

  /** Breakdown sous-zones par entrepôt pour le drill-down expandable.
   *  Map<entrepotName, [{ subEntrepot, total, quantite }]>. Construit
   *  uniquement quand dimension = entrepot ET le snapshot courant est
   *  chargé. Les détails sans sous-zone (stock directement sur l'entrepôt
   *  parent) sont regroupés sous "Stock direct entrepôt". */
  subEntrepotsByEntrepot = computed<
    Map<string, { subEntrepot: string; total: number; quantite: number }[]>
  >(() => {
    const map = new Map<
      string,
      Map<string, { total: number; quantite: number }>
    >();
    const data = this.currentValeur();
    if (!data || this.dimension() !== "entrepot") return new Map();
    for (const d of data.details ?? []) {
      const subKey = d.subEntrepot ?? "Stock direct entrepôt";
      const ent = map.get(d.entrepot) ?? new Map();
      const cur = ent.get(subKey) ?? { total: 0, quantite: 0 };
      cur.total += d.total;
      cur.quantite += d.quantite;
      ent.set(subKey, cur);
      map.set(d.entrepot, ent);
    }
    const result = new Map<
      string,
      { subEntrepot: string; total: number; quantite: number }[]
    >();
    for (const [ent, subs] of map.entries()) {
      const arr = Array.from(subs.entries())
        .map(([subEntrepot, v]) => ({ subEntrepot, ...v }))
        .sort((a, b) => b.total - a.total);
      // Ne pas afficher de drill-down si une seule sous-entrée et qu'elle
      // est "Stock direct entrepôt" — pas d'info utile à montrer.
      if (arr.length === 1 && arr[0].subEntrepot === "Stock direct entrepôt") {
        continue;
      }
      result.set(ent, arr);
    }
    return result;
  });

  toggleExpand(entrepotName: string) {
    const cur = new Set(this.expandedEntrepots());
    if (cur.has(entrepotName)) cur.delete(entrepotName);
    else cur.add(entrepotName);
    this.expandedEntrepots.set(cur);
  }

  hasSubEntrepots(entrepotName: string): boolean {
    return this.subEntrepotsByEntrepot().has(entrepotName);
  }

  /** Vrai si la ligne peut afficher des sous-zones expandables (uniquement
   *  en dimension entrepot, et si le snapshot courant a au moins 2 sous-
   *  entrées différentes pour cet entrepôt). */
  canExpandRow(entrepotName: string): boolean {
    return this.dimension() === "entrepot" && this.hasSubEntrepots(entrepotName);
  }

  /** Wrapper pour le (click) — n'agit que si la ligne est expandable, pour
   *  laisser inerte les lignes des autres dimensions (sans handler clickable). */
  onRowClick(entrepotName: string) {
    if (this.canExpandRow(entrepotName)) {
      this.toggleExpand(entrepotName);
    }
  }

  isExpanded(entrepotName: string): boolean {
    return this.expandedEntrepots().has(entrepotName);
  }

  getSubEntrepots(entrepotName: string) {
    return this.subEntrepotsByEntrepot().get(entrepotName) ?? [];
  }

  // ─── Affichage ───────────────────────────────────────────────────────────

  totalValeur = computed(() => {
    const d = this.payload();
    if (!d) return "—";
    return WidgetHelperService.formatNumeric(d.delta.endValue) + " €";
  });

  deltaText = computed(() => {
    const d = this.payload();
    if (!d) return "";
    const sign = d.delta.absolute >= 0 ? "+" : "−";
    const abs = Math.abs(d.delta.absolute);
    const pct = d.delta.percent;
    const pctStr = pct == null ? "" : ` (${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%)`;
    return `${sign}${WidgetHelperService.formatNumeric(abs)} €${pctStr}`;
  });

  deltaClass = computed<"positive" | "negative" | "neutral">(() => {
    const d = this.payload();
    if (!d || d.delta.absolute === 0) return "neutral";
    return d.delta.absolute > 0 ? "positive" : "negative";
  });

  /** Top N dimensions par valeur de fin, plus une ligne "Autres" agrégée
   *  si plus de N. Pour la dimension `total` on a déjà une seule série.
   *  Si `selectedKeys` est non vide, on bypass le TOP N et on n'affiche
   *  QUE les éléments sélectionnés (pas de regroupement "Autres"). */
  visibleSeries = computed(() => {
    const d = this.payload();
    if (!d) return [];
    if (this.dimension() === "total") {
      return d.series.map((s, i) => ({ ...s, color: PALETTE[i % PALETTE.length] }));
    }
    const sorted = [...d.series].sort((a, b) => {
      const av = a.points[a.points.length - 1]?.total ?? 0;
      const bv = b.points[b.points.length - 1]?.total ?? 0;
      return bv - av;
    });
    const selected = this.selectedKeys();
    if (selected.length > 0) {
      const wanted = new Set(selected);
      return sorted
        .filter((s) => wanted.has(s.dimensionKey))
        .map((s, i) => ({ ...s, color: PALETTE[i % PALETTE.length] }));
    }
    const top = sorted.slice(0, TOP_N).map((s, i) => ({
      ...s,
      color: PALETTE[i % PALETTE.length],
    }));
    if (sorted.length > TOP_N) {
      // Agrégation "Autres" — on additionne points par date.
      const dateMap = new Map<string, number>();
      for (const s of sorted.slice(TOP_N)) {
        for (const p of s.points) {
          dateMap.set(p.date, (dateMap.get(p.date) ?? 0) + p.total);
        }
      }
      const otherPoints = Array.from(dateMap.entries())
        .map(([date, total]) => ({ date, total, quantite: 0 }))
        .sort((a, b) => a.date.localeCompare(b.date));
      top.push({
        dimensionKey: "__OTHERS__",
        dimensionLabel: `Autres (${sorted.length - TOP_N})`,
        points: otherPoints,
        color: "#cccccc",
      });
    }
    return top;
  });

  /** Toutes les dates uniques de la fenêtre, triées ASC — sert de `labels`
   *  pour le chart et de colonnes pour le tableau. */
  allDates = computed(() => {
    const set = new Set<string>();
    for (const s of this.visibleSeries()) {
      for (const p of s.points) set.add(p.date);
    }
    return Array.from(set).sort();
  });

  needsBackfill = computed<boolean>(() => {
    const d = this.payload();
    if (!d) return false;
    if ((d.total?.length ?? 0) === 0) return true;
    if (d.availableSince && d.availableSince > d.startDate) return true;
    return false;
  });

  constructor() {
    // Refetch sur changement de range / granularity / dimension
    effect(
      () => {
        this.range();
        this.granularity();
        this.dimension();
        this.fetch();
      },
      { allowSignalWrites: true },
    );

    // (Re)build le chart
    effect(
      () => {
        const series = this.visibleSeries();
        const labels = this.allDates();
        if (this.canvasRef() && series.length) {
          this.buildChart(series, labels);
        }
      },
      { allowSignalWrites: true },
    );

    // Si la plage change, ajuste la granularité par défaut au plus large
    // de ce que la plage permet — évite l'erreur "granularité incompatible".
    effect(
      () => {
        const allowed = this.granularityElements().map((g) => g.value);
        if (!allowed.includes(this.granularity())) {
          this.granularity.set(allowed[0]);
        }
      },
      { allowSignalWrites: true },
    );

    // Reset du filtre quand la dimension change : les `dimensionKey`
    // disponibles sont propres à chaque dimension, garder une sélection
    // d'articles quand on bascule sur "Entrepôt" n'a aucun sens.
    effect(
      () => {
        this.dimension();
        this.selectedKeys.set([]);
        this.expandedEntrepots.set(new Set());
      },
      { allowSignalWrites: true },
    );

    // Charge le snapshot VALEUR_PAR_FAMILLE courant — source du drill-down
    // par sous-zone (PDC, expé, NC) en dimension entrepot. On le fetch
    // dès l'ouverture du dialog (et pas seulement quand dimension change),
    // pour qu'il soit prêt si l'utilisateur passe sur "Entrepôt" plus tard.
    this.fetchCurrentValeur();

    // Retry si la première fetch a échoué (réseau, back en restart) et
    // que l'utilisateur passe en dimension entrepot.
    effect(
      () => {
        if (this.dimension() === "entrepot" && !this.currentValeur()) {
          this.fetchCurrentValeur();
        }
      },
      { allowSignalWrites: true },
    );
  }

  /** Fetch idempotent — peut être rappelée si la première a foiré (réseau,
   *  back qui redémarre…). */
  private fetchCurrentValeur() {
    this.api.loadOne(WidgetType.VALEUR_PAR_FAMILLE).subscribe({
      next: (res) => {
        const d = res?.data?.data as VALEUR_PAR_FAMILLE_DATA;
        this.currentValeur.set(d ?? null);
      },
      error: () => {
        // Retry automatique si l'utilisateur passe en dimension entrepot
        // alors qu'on n'a pas réussi à charger (cf. effect ci-dessous).
      },
    });
  }

  ngAfterViewInit(): void {
    // Le effect de rebuild peut tirer avant viewchild prêt, on retrigger
    if (this.canvasRef() && this.visibleSeries().length) {
      this.buildChart(this.visibleSeries(), this.allDates());
    }
  }

  // ─── Données ─────────────────────────────────────────────────────────────

  private resolveRange(range: BackfillRange): {
    startDate: string;
    endDate: string;
  } {
    const today = new Date();
    let endDate = format(today, "yyyy-MM-dd");
    let startDate: string;
    switch (range) {
      case "1m": startDate = format(subDays(today, 30), "yyyy-MM-dd"); break;
      case "3m": startDate = format(subDays(today, 90), "yyyy-MM-dd"); break;
      case "6m": startDate = format(subDays(today, 180), "yyyy-MM-dd"); break;
      case "1y": startDate = format(subDays(today, 365), "yyyy-MM-dd"); break;
      case "ytd":
        startDate = format(startOfYear(today), "yyyy-MM-dd");
        break;
      case "exercice":
        startDate = format(startOfYear(today), "yyyy-MM-dd");
        endDate = `${today.getFullYear()}-12-31`;
        break;
      case "exerciceN1": {
        const y = today.getFullYear() - 1;
        startDate = `${y}-01-01`;
        endDate = `${y}-12-31`;
        break;
      }
    }
    return { startDate, endDate };
  }

  fetch() {
    this.loading.set(true);
    const { startDate, endDate } = this.resolveRange(this.range());
    this.api
      .loadEvolutionValeur(this.granularity(), this.dimension(), startDate, endDate)
      .subscribe({
        next: (data) => {
          this.payload.set(data);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  /**
   * Export Excel multi-feuilles : refait un fetch pour chaque dimension
   * (parallèle), construit le workbook (1 feuille par dim + chart PNG +
   * Synthèse + Données brutes) et déclenche le téléchargement.
   */
  /** Ouvre le dialog de confirmation puis lance l'export avec la plage
   *  validée (preset, ou plage personnalisée si l'utilisateur a basculé). */
  async exportExcel() {
    if (this.exporting()) return;

    const ref = this.dialogs.open(
      ExcelExportConfirmDialogComponent,
      "fit-content",
      "fit-content",
      {
        title: "Exporter — Évolution stock",
        periodOptions: this.rangeElements,
        currentPeriod: this.range(),
        allowCustomRange: true,
      } as ExcelExportConfirmData,
    );
    const confirm = (await firstValueFrom(ref.afterClosed())) as
      | ExcelExportConfirmResult
      | null;
    if (!confirm) return;

    this.exporting.set(true);
    let startDate: string;
    let endDate: string;
    if (confirm.customStart && confirm.customEnd) {
      startDate = confirm.customStart;
      endDate = confirm.customEnd;
    } else {
      const r = (confirm.period as BackfillRange) ?? this.range();
      const range = this.resolveRange(r);
      startDate = range.startDate;
      endDate = range.endDate;
    }
    const g = this.granularity();
    const fetchDim = (dim: EvolutionDimension) =>
      this.api.loadEvolutionValeur(g, dim, startDate, endDate);

    // En plus des séries temporelles, on charge l'état courant via le
    // widget VALEUR_PAR_FAMILLE existant — sa réponse contient les
    // `details: ValeurStockDetail[]` (un par lot/stock) qui permettent
    // de construire les drill-downs hiérarchiques (famille → article →
    // lot, entrepôt → article → lot…). Sans ça, l'historique est
    // top-level uniquement (la table de snapshot ne stocke que les
    // agrégats par dimension, pas la décomposition fine).
    forkJoin({
      total: fetchDim("total"),
      famille: fetchDim("famille"),
      sous_famille: fetchDim("sous_famille"),
      article: fetchDim("article"),
      entrepot: fetchDim("entrepot"),
      owner: fetchDim("owner"),
      current: this.api.loadOne(WidgetType.VALEUR_PAR_FAMILLE),
    }).subscribe({
      next: async (res) => {
        try {
          const currentData = res.current?.data?.data as VALEUR_PAR_FAMILLE_DATA;
          await exportEvolutionValeurExcel({
            total: res.total,
            famille: res.famille,
            sous_famille: res.sous_famille,
            article: res.article,
            entrepot: res.entrepot,
            owner: res.owner,
            current: currentData,
            granularity: g,
            startDate,
            endDate,
          });
        } finally {
          this.exporting.set(false);
        }
      },
      error: () => this.exporting.set(false),
    });
  }

  refreshNow() {
    if (this.refreshing()) return;
    this.refreshing.set(true);
    this.api.runBackfill(this.range()).subscribe({
      next: (res) => {
        this.snack.success(
          TranslateUtils.translate("dashboard_ui.evolution_valeur_backfilled", {
            days: res.daysProcessed,
            count: res.rowsInserted,
            mouvements: res.mouvementsProcessed,
          }),
        );
        this.fetch();
        this.refreshing.set(false);
      },
      error: () => this.refreshing.set(false),
    });
  }

  // ─── Chart ───────────────────────────────────────────────────────────────

  private buildChart(
    series: Array<{ dimensionKey: string; dimensionLabel: string; color: string; points: { date: string; total: number }[] }>,
    labels: string[],
  ) {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (this.chart) this.chart.destroy();

    // Map date → valeur pour chaque série, en remplissant les trous avec
    // la dernière valeur connue (forward-fill) — évite les "lignes
    // pointillées" quand une dimension n'a pas de point sur une période.
    const datasets = series.map((s) => {
      const map = new Map(s.points.map((p) => [p.date, p.total]));
      let lastVal: number | null = null;
      const data = labels.map((d) => {
        const v = map.get(d);
        if (v != null) lastVal = v;
        return lastVal ?? null;
      });
      return {
        label: s.dimensionLabel,
        data,
        borderColor: s.color,
        backgroundColor: s.color + "22",
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.2,
        spanGaps: true,
        fill: this.dimension() === "total",
      };
    });

    this.chart = new Chart(ctx, {
      type: "line",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: this.dimension() !== "total", position: "bottom" },
          tooltip: {
            callbacks: {
              label: (item) => {
                const v = item.parsed.y as number;
                return `${item.dataset.label}: ${WidgetHelperService.formatNumeric(v)} €`;
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              autoSkip: true,
              maxTicksLimit: 8,
              font: { size: 10 },
            },
            grid: { display: false },
          },
          y: {
            beginAtZero: false,
            ticks: {
              callback: (v) => WidgetHelperService.formatNumeric(v as number) + " €",
              font: { size: 10 },
            },
            grid: { color: "rgba(0,0,0,0.06)" },
          },
        },
      },
    });
  }

  // ─── Helpers template ────────────────────────────────────────────────────

  formatVal(v: number | null | undefined): string {
    if (v == null) return "—";
    return WidgetHelperService.formatNumeric(v) + " €";
  }

  /** Dernière valeur d'une série (pour le tableau). */
  lastValue(points: { date: string; total: number }[]): number {
    return points[points.length - 1]?.total ?? 0;
  }

  /** Variation (delta absolu) d'une série sur la fenêtre. */
  seriesDelta(points: { date: string; total: number }[]): {
    val: number;
    pct: number | null;
    cls: "positive" | "negative" | "neutral";
  } {
    if (points.length === 0) return { val: 0, pct: null, cls: "neutral" };
    const start = points[0].total;
    const end = points[points.length - 1].total;
    const val = end - start;
    const pct = start === 0 ? null : Math.round((val / start) * 1000) / 10;
    const cls: "positive" | "negative" | "neutral" =
      val === 0 ? "neutral" : val > 0 ? "positive" : "negative";
    return { val, pct, cls };
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
